// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 26-versioning-update-strategy.md#capacity-changes-from-the-dashboard
// @greenlight-spec: 19-design-system.md
import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Minus, Plus } from 'lucide-react';

import { relativeTime } from '@/components/greenlight/relativeTime';
import { StatusBadge, type StatusBadgeVariant } from '@/components/greenlight/StatusBadge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/lib/api';
import { HEALTH_POLL_MS, RUN_ACTIVE_POLL_MS } from '@/lib/healthPoll';
import {
  DASHBOARD_CAPACITY_KEYS,
  DASHBOARD_NODE_COUNT_LIMIT,
  type CapacityChange,
  type CapacityChangeSchedule,
  type CapacityChangeSource,
  type CapacityPosture,
  type CapacityValues,
  type DashboardCapacityKey,
} from '@greenlight/shared';

export const CAPACITY_KEY = ['health-capacity'] as const;

const GENERIC_REFUSAL = 'The change could not be recorded.';

/** How each count reads in a sentence about a change. */
const CHANGE_LABELS: Record<DashboardCapacityKey, string> = {
  appNodeMaxCount: 'limit',
  appNodeMinCount: 'always on',
  systemNodeCount: "Greenlight's nodes",
};

const STATUS_META: Record<
  CapacityChange['status'],
  { label: string; variant: StatusBadgeVariant }
> = {
  pending: { label: 'Waiting for an update run', variant: 'pending' },
  applying: { label: 'Applying', variant: 'deploying' },
  applied: { label: 'applied', variant: 'active' },
  failed: { label: 'not applied', variant: 'failed' },
  superseded: { label: 'replaced', variant: 'inactive' },
  declined: { label: 'declined', variant: 'inactive' },
};

const nodes = (count: number): string => `${count} ${count === 1 ? 'node' : 'nodes'}`;

function describeChange(change: CapacityChange): string {
  return Object.entries(change.requested)
    .map(
      ([key, value]) =>
        `${CHANGE_LABELS[key as DashboardCapacityKey]} ${change.previous[key as DashboardCapacityKey]} → ${value}`,
    )
    .join(', ');
}

/** App nodes running now that a lower limit would remove; 0 when it removes none. */
export function appNodesRemoved(
  values: Partial<CapacityValues>,
  effective: CapacityValues,
  running: number | null,
): number {
  const max = values.appNodeMaxCount;
  if (max === undefined || max >= effective.appNodeMaxCount || running === null) return 0;
  return Math.max(0, running - max);
}

/** One sentence per changed count, in the words the stepper uses. */
export function describeRequest(
  values: Partial<CapacityValues>,
  effective: CapacityValues,
  removed: number,
): string {
  const parts: string[] = [];
  if (values.appNodeMaxCount !== undefined) {
    parts.push(`Can grow to ${nodes(values.appNodeMaxCount)} (now ${effective.appNodeMaxCount}).`);
  }
  if (values.appNodeMinCount !== undefined) {
    parts.push(
      `Keeps ${nodes(values.appNodeMinCount)} always on (now ${effective.appNodeMinCount}).`,
    );
  }
  if (values.systemNodeCount !== undefined) {
    parts.push(
      `Greenlight runs on ${nodes(values.systemNodeCount)} (now ${effective.systemNodeCount}).`,
    );
  }
  parts.push(
    removed > 0
      ? `${removed === 1 ? '1 running node is' : `${removed} running nodes are`} removed, and ${removed === 1 ? 'its' : 'their'} apps move to the nodes that stay.`
      : 'Nothing running stops.',
  );
  return parts.join(' ');
}

function refusalMessage(body: unknown): string {
  const message = (body as { message?: unknown } | null)?.message;
  return typeof message === 'string' && message !== '' ? message : GENERIC_REFUSAL;
}

function refusalCode(body: unknown): string | null {
  const code = (body as { details?: { refusal?: unknown } } | null)?.details?.refusal;
  return typeof code === 'string' ? code : null;
}

/** The ts-rest client throws a non-2xx response as `{ status, body }`; a network failure has no body. */
function thrownBody(err: unknown): unknown {
  return err !== null && typeof err === 'object' && 'body' in err
    ? (err as { body: unknown }).body
    : null;
}

/** ts-rest throws a non-2xx read out of the query as the response itself. */
function errorStatus(error: unknown): number | null {
  const status = (error as { status?: unknown } | null)?.status;
  return typeof status === 'number' ? status : null;
}

interface PendingRequest {
  values: Partial<CapacityValues>;
  source: CapacityChangeSource;
}

function Stepper({
  id,
  label,
  hint,
  value,
  min,
  max,
  disabled,
  onChange,
}: {
  readonly id: string;
  readonly label: string;
  readonly hint?: string;
  readonly value: number;
  readonly min: number;
  readonly max: number;
  readonly disabled: boolean;
  readonly onChange: (value: number) => void;
}): React.ReactElement {
  return (
    <div className="space-y-1" data-testid={`capacity-${id}`}>
      <div className="flex flex-wrap items-center gap-3">
        <span id={`capacity-${id}-label`} className="min-w-[9.5rem] text-sm text-foreground">
          {label}
        </span>
        <div
          role="group"
          aria-labelledby={`capacity-${id}-label`}
          className="flex items-center gap-2"
        >
          <Button
            type="button"
            size="icon"
            variant="outline"
            className="size-9"
            aria-label={`${label}: one fewer`}
            disabled={disabled || value <= min}
            onClick={() => onChange(value - 1)}
          >
            <Minus className="size-4" aria-hidden="true" />
          </Button>
          <output
            aria-live="polite"
            className="min-w-[5.5rem] text-center font-mono text-sm tabular-nums text-foreground"
          >
            {nodes(value)}
          </output>
          <Button
            type="button"
            size="icon"
            variant="outline"
            className="size-9"
            aria-label={`${label}: one more`}
            disabled={disabled || value >= max}
            onClick={() => onChange(value + 1)}
          >
            <Plus className="size-4" aria-hidden="true" />
          </Button>
        </div>
      </div>
      {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  );
}

/**
 * Changes how far the cluster can grow, under the nodes it changes (docs/28
 * §/internal-health). The page records a request; the updater applies it now or
 * at the next update window. The server checks every change, so a refusal is
 * shown in the server's words.
 */
export function NodeCounts(): React.ReactElement {
  const queryClient = useQueryClient();
  const query = api.health.capacityPosture.useQuery(CAPACITY_KEY, undefined, {
    queryKey: CAPACITY_KEY,
    refetchInterval: ({ state }) => {
      const body = state.data?.status === 200 ? state.data.body : null;
      return body?.open_change ? RUN_ACTIVE_POLL_MS : HEALTH_POLL_MS;
    },
  });
  const requestChange = api.health.requestCapacityChange.useMutation();
  const declineAsk = api.health.declineCapacityAsk.useMutation();

  const [draft, setDraft] = React.useState<CapacityValues | null>(null);
  const [seededFrom, setSeededFrom] = React.useState<string | null>(null);
  const [quick, setQuick] = React.useState<PendingRequest | null>(null);
  const [moreOpen, setMoreOpen] = React.useState(false);
  const [nowOnly, setNowOnly] = React.useState<string | null>(null);
  const [refusal, setRefusal] = React.useState<string | null>(null);
  const [outcome, setOutcome] = React.useState<{ kind: 'ok' | 'refused'; text: string } | null>(
    null,
  );

  const posture: CapacityPosture | null = query.data?.status === 200 ? query.data.body : null;

  // Re-seeded whenever the install's values move, so a change that lands (or one
  // made outside the dashboard) never leaves the stepper proposing the old values.
  const effectiveKey = posture?.effective ? JSON.stringify(posture.effective) : null;
  React.useEffect(() => {
    if (!posture?.effective || effectiveKey === null) return;
    if (draft !== null && effectiveKey === seededFrom) return;
    setDraft(posture.effective);
    setSeededFrom(effectiveKey);
  }, [posture, draft, effectiveKey, seededFrom]);

  if (query.isLoading) {
    return (
      <div className="mt-4 border-t border-border pt-4" data-testid="capacity">
        <Skeleton className="h-9 w-full" aria-label="Loading node limits" />
      </div>
    );
  }
  if (!posture) {
    const forbidden = errorStatus(query.error) === 403;
    return (
      <div className="mt-4 border-t border-border pt-4" data-testid="capacity">
        {forbidden ? (
          <p className="text-xs text-muted-foreground" data-testid="capacity-forbidden">
            Only organization admins can see and change how far the cluster can grow.
          </p>
        ) : (
          <div
            className="flex flex-wrap items-center gap-3"
            data-testid="capacity-error"
            role="alert"
          >
            <p className="text-xs text-[hsl(var(--status-red-text))]">
              The node limits could not be loaded, so they cannot be changed right now.
            </p>
            <Button
              size="sm"
              variant="secondary"
              disabled={query.isFetching}
              onClick={() => void query.refetch()}
            >
              Try again
            </Button>
          </div>
        )}
      </div>
    );
  }

  const effective = posture.effective;
  const open = posture.open_change;
  const editable =
    posture.updater_enabled === true && effective !== null && open?.status !== 'applying';
  const pending = requestChange.isPending || declineAsk.isPending;

  const draftValues: Partial<CapacityValues> = {};
  if (draft && effective) {
    for (const key of DASHBOARD_CAPACITY_KEYS) {
      if (draft[key] !== effective[key]) draftValues[key] = draft[key];
    }
  }
  const request: PendingRequest | null =
    quick ??
    (Object.keys(draftValues).length > 0 ? { values: draftValues, source: 'manual' } : null);
  const shownRefusal = refusal && request ? refusal : null;
  const removed =
    request && effective
      ? appNodesRemoved(request.values, effective, posture.app_pool_node_count)
      : 0;
  const windowOffered = removed === 0 && nowOnly === null;

  const reset = (): void => {
    setQuick(null);
    setNowOnly(null);
    setRefusal(null);
    if (effective) setDraft(effective);
  };

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: CAPACITY_KEY });
  };

  const submit = (req: PendingRequest, schedule: CapacityChangeSchedule): void => {
    setOutcome(null);
    const refused = (body: unknown): void => {
      if (refusalCode(body) === 'capacity.apply_now_only') {
        setNowOnly(refusalMessage(body));
        return;
      }
      setRefusal(refusalMessage(body));
    };
    requestChange.mutate(
      { body: { values: req.values, schedule, source: req.source } },
      {
        onSuccess: (result) => {
          if (result.status >= 300) {
            refused(result.body);
            return;
          }
          const body = result.body;
          setOutcome({
            kind: body.change.status === 'failed' ? 'refused' : 'ok',
            text:
              body.trigger_refusal ??
              (schedule === 'now'
                ? 'Recorded. An update run started and applies it now.'
                : 'Recorded. The next update run applies it.'),
          });
          setQuick(null);
          setNowOnly(null);
          setRefusal(null);
          setDraft(null);
          refresh();
        },
        onError: (err) => refused(thrownBody(err)),
      },
    );
  };

  const decline = (proposed: number): void => {
    setOutcome(null);
    declineAsk.mutate(
      { body: { key: 'appNodeMaxCount', proposed } },
      {
        onSuccess: (result) => {
          if (result.status >= 300) {
            setOutcome({ kind: 'refused', text: refusalMessage(result.body) });
            return;
          }
          reset();
          setOutcome({ kind: 'ok', text: 'Greenlight will not ask again for a week.' });
          refresh();
        },
        onError: (err) => setOutcome({ kind: 'refused', text: refusalMessage(thrownBody(err)) }),
      },
    );
  };

  const setCount = (key: DashboardCapacityKey, value: number): void => {
    if (!draft) return;
    setQuick(null);
    setNowOnly(null);
    setRefusal(null);
    setDraft({ ...draft, [key]: value });
  };

  const last = posture.recent_changes.find(
    (change) => change.status !== 'declined' && change.status !== 'superseded',
  );

  return (
    <div className="mt-4 space-y-3 border-t border-border pt-4" data-testid="capacity">
      {posture.ask ? (
        <div
          className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-md border border-border bg-muted/40 px-3 py-2"
          data-testid="capacity-ask"
        >
          <p className="text-sm text-foreground">
            Apps are waiting for room: the cluster is at its limit of {nodes(posture.ask.current)}.
          </p>
          <div className="flex gap-2">
            <Button
              size="sm"
              disabled={pending || !editable}
              onClick={() => {
                setRefusal(null);
                setNowOnly(null);
                setQuick({ values: { appNodeMaxCount: posture.ask!.proposed }, source: 'ask' });
              }}
            >
              Raise to {posture.ask.proposed}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={pending}
              onClick={() => decline(posture.ask!.proposed)}
            >
              Not now
            </Button>
          </div>
        </div>
      ) : posture.suggestion ? (
        <div
          className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-md border border-border bg-muted/40 px-3 py-2"
          data-testid="capacity-suggestion"
        >
          <p className="text-sm text-foreground">
            For 30 days your apps have fit on fewer than the {nodes(posture.suggestion.current)}{' '}
            kept always on.
          </p>
          <Button
            size="sm"
            variant="secondary"
            disabled={pending || !editable}
            onClick={() => {
              setRefusal(null);
              setNowOnly(null);
              setQuick({
                values: { appNodeMinCount: posture.suggestion!.proposed },
                source: 'suggestion',
              });
            }}
          >
            Keep {posture.suggestion.proposed} always on
          </Button>
        </div>
      ) : null}

      {posture.updater_enabled === false ? (
        <p className="text-xs text-muted-foreground">
          The updater is off on this install, so nothing would apply a change here.
        </p>
      ) : posture.updater_enabled === null ? (
        <p className="text-xs text-muted-foreground">
          Can’t tell whether the updater is on: the cluster could not be read. Changes wait until it
          can.
        </p>
      ) : effective === null ? (
        <p className="text-xs text-muted-foreground">
          The install has not recorded its node limits yet. The next update run records them.
        </p>
      ) : null}

      {draft ? (
        <Stepper
          id="max"
          label="Can grow to"
          value={draft.appNodeMaxCount}
          min={Math.max(1, draft.appNodeMinCount)}
          max={DASHBOARD_NODE_COUNT_LIMIT}
          disabled={!editable || pending}
          onChange={(value) => setCount('appNodeMaxCount', value)}
        />
      ) : null}

      {request && effective ? (
        <div
          className="space-y-2 rounded-md border border-border px-3 py-2"
          data-testid="capacity-confirm"
        >
          {nowOnly ? <p className="text-xs text-muted-foreground">{nowOnly}</p> : null}
          <p className="text-sm text-foreground">
            {describeRequest(request.values, effective, removed)}
          </p>
          {shownRefusal ? (
            <p
              role="alert"
              data-testid="capacity-refusal"
              className="text-sm text-[hsl(var(--status-red-text))]"
            >
              {shownRefusal}
            </p>
          ) : null}
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              disabled={pending || !editable}
              onClick={() => submit(request, 'now')}
            >
              {pending ? 'Recording…' : 'Apply now'}
            </Button>
            {windowOffered ? (
              <Button
                size="sm"
                variant="secondary"
                disabled={pending || !editable}
                onClick={() => submit(request, 'window')}
              >
                At the next update window
              </Button>
            ) : null}
            <Button size="sm" variant="ghost" disabled={pending} onClick={reset}>
              Cancel
            </Button>
          </div>
        </div>
      ) : null}

      {outcome ? (
        <p
          role="status"
          className={`text-xs ${outcome.kind === 'refused' ? 'text-[hsl(var(--status-red-text))]' : 'text-muted-foreground'}`}
        >
          {outcome.text}
        </p>
      ) : null}

      {open ? (
        <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="capacity-open">
          <StatusBadge variant={STATUS_META[open.status].variant}>
            {STATUS_META[open.status].label}
          </StatusBadge>
          <span className="text-muted-foreground">{describeChange(open)}</span>
        </div>
      ) : last ? (
        <p className="text-xs text-muted-foreground" data-testid="capacity-last">
          Last change: {describeChange(last)}, {STATUS_META[last.status].label}{' '}
          {relativeTime(last.finished_at ?? last.created_at)}
          {last.status === 'failed' && last.reason ? ` (${last.reason})` : ''}.
        </p>
      ) : null}

      {draft ? (
        <div className="space-y-3">
          <button
            type="button"
            className="text-xs font-medium text-foreground underline underline-offset-2"
            aria-expanded={moreOpen}
            onClick={() => setMoreOpen(!moreOpen)}
          >
            {moreOpen ? 'Fewer options' : 'More options'}
          </button>
          {moreOpen ? (
            <div className="space-y-3" data-testid="capacity-more">
              <Stepper
                id="min"
                label="Always on"
                hint="Nodes kept running even when apps are idle. The cluster never shrinks below this."
                value={draft.appNodeMinCount}
                min={0}
                max={draft.appNodeMaxCount}
                disabled={!editable || pending}
                onChange={(value) => setCount('appNodeMinCount', value)}
              />
              <Stepper
                id="system"
                label="Greenlight's nodes"
                hint="Nodes that run Greenlight itself. Add one to keep Greenlight up if a node fails; removing one is not available yet."
                value={draft.systemNodeCount}
                min={effective?.systemNodeCount ?? draft.systemNodeCount}
                max={DASHBOARD_NODE_COUNT_LIMIT}
                disabled={!editable || pending}
                onChange={(value) => setCount('systemNodeCount', value)}
              />
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
